import { useState, useEffect, FormEvent } from 'react';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router';
import * as validation from 'myParkdUtils/validationHandling';
import UpperNavBar from 'components/board/UpperNavBar';
import LoadingSpinner from 'components/LoadingSpinner';
import ErrorMessage from 'components/ErrorMessage';
import { ReduxState } from 'types';
import { authSlice } from 'features/auth/authSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { GAAction, trackDataLayerEvent } from 'myParkdUtils/googleAnalytics';
import CookieConsentButton from 'components/CookieConsentButton';


export const LoginScreen = (props: Props) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);

    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const token = props.auth.token;

    useEffect(() => {
        if (token) {
            navigate('/vehicles');
        }
    }, [token]);

    const login = async () => {
        props.login({ email, password });
    };

    const getValidationErrors = () => {
        let errors: any = {};
        errors.email = validation.checkIfValidEmailAddress(email);
        errors.password = validation.checkForMinimumLength(password, 6);
        return errors;
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const validationSuccessful = validation.validate(getValidationErrors());
        if (validationSuccessful) {
            login();
        } else {
            setShowValidationErrors(true);
        }
    };

    const errors = getValidationErrors();
    const validationSuccessful = validation.validate(errors);

    // temporary test for marketing (conversion tracking)
    const testGTMEvent = () => {
        trackDataLayerEvent({
            event: GAAction.REGISTRATION_SINGLE_STEP_COMPLETED,
            registrationStep: 'step_test'
        });
    }

    return (
        <div className='page bg-blue'>
            <div className='grid-viewport'>
                <UpperNavBar />
            </div>

            <div id="screen-login" className="panel radius-rectangle full-width-form">
                <div className="panel-header">
                    <h1 className="bold-title">
                        <FormattedMessage id="LOGIN_WELCOME_TO" />&nbsp;
                        <span onClick={testGTMEvent} className="myparkd">MyParkd</span>
                    </h1>
                </div>
                <div className="panel-content">
                    {props.auth.loading ? (
                        <LoadingSpinner message={<FormattedMessage id="LOGIN_LOGGING_IN" />} />
                    ) : (
                        <div>
                            <form
                                onSubmit={handleSubmit}
                                noValidate
                                className={
                                    (validationSuccessful ? 'validation-successful ' : '') +
                                    (showValidationErrors ? 'show-validation-messages' : 'hide-validation-messages')
                                }
                            >
                                {props.auth.error && (
                                    <div className="error-box center">
                                        <FormattedMessage id="LOGIN_INVALID_USERNAME_PASSWORD" />
                                    </div>
                                )}

                                <div className="fieldrow">
                                    <input
                                        id="input-email"
                                        type="email"
                                        name="email"
                                        className={'text-field dark-placeholder ' + (errors.email ? 'error' : 'valid')}
                                        placeholder={formatMessage({ id: 'EMAIL' })}
                                        onChange={(e) => setEmail(e.target.value)}
                                        autoComplete="email"
                                        value={email}
                                        required
                                    />

                                    <ErrorMessage msg={errors.email} />
                                </div>

                                <div className="fieldrow">
                                    <div id="input-password-container">
                                        <input
                                            id="input-password"
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            className={'text-field dark-placeholder ' + (errors.password ? 'error' : 'valid')}
                                            placeholder={formatMessage({ id: 'PASSWORD' })}
                                            onChange={(e) => setPassword(e.target.value)}
                                            autoComplete="off"
                                            required
                                        />
                                        <button data-testid="button-toggle-password-visibility" type="button" id="input-password-button-toggle-visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                                        </button>
                                    </div>
                                    <ErrorMessage msg={errors.password} />
                                </div>

                                <button type="submit" className="btn-blue full-width">
                                    <FormattedMessage id="LOGIN_LOGIN_NOW" />
                                </button>
                            </form>

                            <div className="center">
                                <Link to="/reset-password" className="small-gray-link">
                                    <FormattedMessage id="LOGIN_FORGOTTEN_PASSWORD" />
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="panel radius-rectangle">
                <div className="panel-header">
                    <span>
                        <FormattedMessage id="LOGIN_NOT_SIGNED_UP_YET" />
                    </span>
                    <Link to="/register" className="text-link register-link">
                        <FormattedMessage id="LOGIN_REGISTER_NOW" />
                    </Link>
                </div>
            </div>

            <div className='cookie-button-wrapper'>
                <CookieConsentButton />
            </div>

        </div>
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        auth: state.auth,
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        login: authSlice.actions.login,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginScreen);